import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent implements ControlValueAccessor {
  @Input() placeholder = '';
  @Input() label = '';
  @Input() id = '';
  @Input() name = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() errorMessage = '';
  @Input() options: { value: string | number; label: string }[] = [];
  @Input() hasIcon = false;
  @Input() iconPosition: 'left' | 'right' = 'left';
  @Input() set value(val: string | number) {
    this._value = val;
  }

  get value(): string | number {
    return this._value;
  }

  @Output() valueChange = new EventEmitter<string | number>();
  @Output() blur = new EventEmitter<FocusEvent>();
  @Output() focus = new EventEmitter<FocusEvent>();

  private _value: string | number = '';
  private _touched = false;

  onChange: any = () => {};
  onTouched: any = () => {};

  onSelectChange(event: any) {
    // Material Select emits MatSelectChange event with value property
    this._value = event.value;
    this.onChange(this._value);
    this.valueChange.emit(this._value);
    this.markAsTouched();
  }

  onSelectBlur(event: FocusEvent) {
    this.markAsTouched();
    this.blur.emit(event);
  }

  // Computed property to check if the select has errors
  get hasError(): boolean {
    // Show error only if field is touched AND errorMessage is provided
    return this.errorMessage !== '' && this._touched;
  }

  // Computed property to get the error message
  get errorMsg(): string {
    return this.hasError ? this.errorMessage : '';
  }

  onSelectFocus(event: FocusEvent) {
    this.focus.emit(event);
  }

  markAsTouched() {
    if (!this._touched) {
      this._touched = true;
      this.onTouched();
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string | number): void {
    this._value = value || '';
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
